import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/* CortexQA loader.
 *
 * One component, three faces, because a loader that depicts a specific activity
 * lies when it is reused for something else:
 *
 *   variant="guide"  a requirements document being scanned, Epic and Story
 *                    cards stacking up out of it — for Create Epic/Story cards
 *   variant="xray"   the agent thinking while test steps write themselves
 *                    underneath it — for Build Xray tests
 *   variant="spark"  abstract, the app's own Spark mark — the default, usable
 *                    over any operation and small enough to sit inline
 *
 * Usage:
 *   <Loader.Inline />                       16px, drops into a button or row
 *   <Loader.Block label="Loading epics…" /> centred in a panel or empty state
 *   <Loader.Overlay variant="guide" />      blocking dialog over a dimmed page
 *   <Loader.Overlay variant="xray" />
 *   <Loader.Overlay />                      the spark default
 *
 * The Overlay is deliberately NOT dismissible: there is no close button, the
 * backdrop ignores clicks, and Escape is swallowed so it cannot close a parent
 * dialog underneath it. Cancelling midway through a generation run would leave
 * half-created Jira issues behind.
 *
 * Animations are CSS, defined once in the document head rather than in
 * tailwind.config.js, so this file is self-contained and can be dropped
 * anywhere. The whole set collapses to a static frame under
 * prefers-reduced-motion.
 */

export const STYLE_ID = "cqa-loader-styles";

/* Exported so the keyframes can be asserted on directly instead of fishing the
   injected <style> element back out of the document. */
export const ANIMATION_CSS = `
.cqa-ld * { transform-box: fill-box; transform-origin: center; }

@keyframes cqa-ld-spin   { to { transform: rotate(360deg); } }
@keyframes cqa-ld-spin-r { to { transform: rotate(-360deg); } }
@keyframes cqa-ld-halo   { 0%,100% { transform: scale(.85); opacity:.22 } 50% { transform: scale(1.5); opacity:0 } }
@keyframes cqa-ld-breathe{ 0%,100% { opacity:1 } 50% { opacity:.5 } }

/* guide: scan bar sweeping the document */
@keyframes cqa-ld-scan   { 0% { transform: translateY(0) } 50% { transform: translateY(78px) } 100% { transform: translateY(0) } }
/* guide: content carried from the document into a card */
@keyframes cqa-ld-carry  { 0% { transform: translate(0,0); opacity:0 }
                           15% { opacity:1 }
                           55% { transform: translate(22px,0); opacity:0 }
                           100% { transform: translate(22px,0); opacity:0 } }
/* guide + xray: a card or a line arriving */
@keyframes cqa-ld-arrive { 0%,8% { opacity:0; transform: translateX(-6px) }
                           18%,88% { opacity:1; transform: translateX(0) }
                           100% { opacity:0; transform: translateX(0) } }
/* guide: indeterminate progress bar */
@keyframes cqa-ld-bar    { 0% { transform: translateX(0) } 50% { transform: translateX(120px) } 100% { transform: translateX(0) } }

/* xray: the head tilting as it thinks */
@keyframes cqa-ld-tilt   { 0%,100% { transform: rotate(-3deg) } 50% { transform: rotate(3deg) } }
/* xray: eye blink */
@keyframes cqa-ld-blink  { 0%,4%,10%,100% { transform: scaleY(1) } 6%,8% { transform: scaleY(.18) } }
/* xray: mouth */
@keyframes cqa-ld-talk   { 0%,100% { transform: scaleX(1) } 50% { transform: scaleX(.55) } }
/* xray: thought dots rising */
@keyframes cqa-ld-rise   { 0% { transform: translateY(4px); opacity:0 }
                           40% { opacity:1 }
                           100% { transform: translateY(-14px); opacity:0 } }
/* xray: a step line writing itself left to right */
@keyframes cqa-ld-write  { 0%,5% { transform: scaleX(0) }
                           28%,90% { transform: scaleX(1) }
                           100% { transform: scaleX(0) } }

.cqa-ld-spin    { animation: cqa-ld-spin 1.3s linear infinite; }
.cqa-ld-orbit   { animation: cqa-ld-spin-r 3.4s linear infinite; }
.cqa-ld-halo    { animation: cqa-ld-halo 2.2s ease-in-out infinite; }
.cqa-ld-breathe { animation: cqa-ld-breathe 1.6s ease-in-out infinite; }
.cqa-ld-scan    { animation: cqa-ld-scan 3.4s ease-in-out infinite; }
.cqa-ld-carry   { animation: cqa-ld-carry 3.4s ease-in-out infinite; }
.cqa-ld-arrive  { animation: cqa-ld-arrive 3.4s ease-in-out infinite; }
.cqa-ld-bar     { animation: cqa-ld-bar 1.8s ease-in-out infinite; }
.cqa-ld-tilt    { animation: cqa-ld-tilt 3.2s ease-in-out infinite; }
.cqa-ld-blink   { animation: cqa-ld-blink 3.4s ease-in-out infinite; }
.cqa-ld-talk    { animation: cqa-ld-talk 2.4s ease-in-out infinite; }
.cqa-ld-rise    { animation: cqa-ld-rise 1.8s ease-out infinite; }
.cqa-ld-write   { transform-origin: left center; animation: cqa-ld-write 3.6s ease-in-out infinite; }

.cqa-ld-d1 { animation-delay: .3s }
.cqa-ld-d2 { animation-delay: .6s }
.cqa-ld-d3 { animation-delay: .9s }
.cqa-ld-d4 { animation-delay: 1.2s }

/* Finished states animate once, on arrival, and then hold still — looping
   motion is what tells a user something is still in progress. */
@keyframes cqa-ld-pop    { 0% { transform: scale(0); opacity: 0 } 70% { transform: scale(1.15) } 100% { transform: scale(1); opacity: 1 } }
@keyframes cqa-ld-settle { 0% { transform: translateX(-6px); opacity: 0 } 100% { transform: translateX(0); opacity: 1 } }

.cqa-ld-pop    { animation: cqa-ld-pop .45s ease-out .25s both; }
.cqa-ld-settle { animation: cqa-ld-settle .35s ease-out both; }

/* Motion is decoration here; the text carries the message. */
@media (prefers-reduced-motion: reduce) {
  .cqa-ld [class*="cqa-ld-"] { animation: none !important; }
  .cqa-ld .cqa-ld-write,
  .cqa-ld .cqa-ld-arrive,
  .cqa-ld .cqa-ld-carry { opacity: 1 !important; transform: none !important; }
}
`;

/* Injected once, on first mount, so the file carries its own animations
   without touching the shared Tailwind config. */
function useLoaderStyles() {
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = ANIMATION_CSS;
    document.head.appendChild(el);
  }, []);
}

/* ------------------------------------------------------------------ */
/*  Artwork                                                            */
/* ------------------------------------------------------------------ */

/* The app's own Spark mark, orbited. Abstract on purpose: this one is shown
   over operations of every kind, so it must not claim to be doing anything
   in particular. */
function SparkArt({ size = 112 }) {
  return (
    <svg
      className="cqa-ld"
      width={size}
      height={size}
      viewBox="0 0 96 96"
      role="presentation"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="cqaLdSpark" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="rgb(var(--brand-600))" />
          <stop offset="100%" stopColor="#7C3AED" />
        </linearGradient>
      </defs>
      <circle cx="48" cy="48" r="38" fill="none" stroke="rgb(var(--brand-100))" strokeWidth="3" />
      <circle cx="48" cy="48" r="20" fill="rgb(var(--brand-600))" className="cqa-ld-halo" />
      <path
        d="M 48 10 A 38 38 0 0 1 86 48"
        fill="none"
        stroke="rgb(var(--brand-600))"
        strokeWidth="3.4"
        strokeLinecap="round"
        className="cqa-ld-spin"
      />
      <g className="cqa-ld-orbit">
        <circle cx="48" cy="10" r="4" fill="#7C3AED" />
      </g>
      <g transform="translate(31.6 31.6) scale(1.37)" className="cqa-ld-breathe">
        <path
          d="M12 2C12 2 14.5 8.5 16 10c1.5 1.5 6 2 6 2s-4.5.5-6 2c-1.5 1.5-4 8-4 8s-2.5-6.5-4-8c-1.5-1.5-6-2-6-2s4.5-.5 6-2C9.5 8.5 12 2 12 2z"
          fill="url(#cqaLdSpark)"
        />
      </g>
    </svg>
  );
}

/* A requirements guide being read, with Epic and Story cards stacking up out
   of it. Deliberately a document and not a browser window — this runs while
   the AI reads a PlantOps help topic, not while it explores a page. */
function GuideScanArt() {
  return (
    <svg
      className="cqa-ld"
      width="330"
      height="125"
      viewBox="0 0 264 100"
      role="presentation"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="cqaLdScan" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgb(var(--brand-500))" stopOpacity="0" />
          <stop offset="50%" stopColor="rgb(var(--brand-500))" stopOpacity="0.65" />
          <stop offset="100%" stopColor="rgb(var(--brand-500))" stopOpacity="0" />
        </linearGradient>
        <clipPath id="cqaLdDoc">
          <rect x="4" y="4" width="68" height="92" rx="7" />
        </clipPath>
      </defs>

      {/* the guide */}
      <rect x="4" y="4" width="68" height="92" rx="7" fill="rgb(var(--card))" stroke="rgb(var(--slate-300))" strokeWidth="1.6" />
      <rect x="14" y="16" width="36" height="6" rx="3" fill="rgb(var(--slate-400))" />
      <rect x="14" y="30" width="50" height="4" rx="2" fill="rgb(var(--slate-200))" />
      <rect x="14" y="40" width="42" height="4" rx="2" fill="rgb(var(--slate-200))" />
      <rect x="14" y="50" width="50" height="4" rx="2" fill="rgb(var(--slate-200))" />
      <rect x="14" y="60" width="30" height="4" rx="2" fill="rgb(var(--slate-200))" />
      <rect x="14" y="70" width="46" height="4" rx="2" fill="rgb(var(--slate-200))" />
      <rect x="14" y="80" width="38" height="4" rx="2" fill="rgb(var(--slate-200))" />
      <g clipPath="url(#cqaLdDoc)">
        <rect x="4" y="4" width="68" height="14" fill="url(#cqaLdScan)" className="cqa-ld-scan" />
      </g>

      {/* content carried across into the cards */}
      <circle cx="78" cy="30" r="3.2" fill="#7C3AED" className="cqa-ld-carry" />
      <circle cx="78" cy="52" r="3.2" fill="#7C3AED" className="cqa-ld-carry cqa-ld-d2" />
      <circle cx="78" cy="74" r="3.2" fill="#7C3AED" className="cqa-ld-carry cqa-ld-d4" />

      {/* the epic, then its stories */}
      <g className="cqa-ld-arrive">
        <rect x="100" y="6" width="160" height="24" rx="7" fill="#F5F3FF" stroke="#C4B5FD" strokeWidth="1.5" />
        <rect x="108" y="14" width="26" height="9" rx="4.5" fill="#7C3AED" />
        <rect x="140" y="15" width="84" height="6" rx="3" fill="#C4B5FD" />
      </g>
      {/* Story cards use blue (not green) to match Jira / the Story Cards
          column in the Requirement Board, so the loader visually previews the
          same colour language the user is about to see. */}
      <g className="cqa-ld-arrive cqa-ld-d2">
        <rect x="112" y="38" width="148" height="24" rx="7" fill="#EFF6FF" stroke="#93C5FD" strokeWidth="1.5" />
        <rect x="120" y="46" width="26" height="9" rx="4.5" fill="#1D4ED8" />
        <rect x="152" y="47" width="70" height="6" rx="3" fill="#93C5FD" />
      </g>
      <g className="cqa-ld-arrive cqa-ld-d4">
        <rect x="112" y="70" width="148" height="24" rx="7" fill="#EFF6FF" stroke="#93C5FD" strokeWidth="1.5" />
        <rect x="120" y="78" width="26" height="9" rx="4.5" fill="#1D4ED8" />
        <rect x="152" y="79" width="80" height="6" rx="3" fill="#93C5FD" />
      </g>
    </svg>
  );
}

/* The agent thinking, with test steps writing themselves underneath. The steps
   matter: they keep a friendly bot head from reading as a chat assistant by
   showing what it is actually producing. */
function BotArt() {
  return (
    <svg
      className="cqa-ld"
      width="212"
      height="187"
      viewBox="0 0 170 150"
      role="presentation"
      aria-hidden="true"
    >
      <g className="cqa-ld-tilt">
        {/* thoughts */}
        <circle cx="111" cy="40" r="2" fill="#C4B5FD" className="cqa-ld-rise" />
        <circle cx="119" cy="34" r="2.6" fill="#A78BFA" className="cqa-ld-rise cqa-ld-d1" />
        <circle cx="128" cy="26" r="3.2" fill="#7C3AED" className="cqa-ld-rise cqa-ld-d2" />

        {/* antenna */}
        <line x1="85" y1="48" x2="85" y2="37" stroke="rgb(var(--slate-400))" strokeWidth="2" strokeLinecap="round" />
        <circle cx="85" cy="33" r="3.4" fill="#7C3AED" className="cqa-ld-breathe" />

        {/* head */}
        <rect x="54" y="48" width="62" height="50" rx="15" fill="rgb(var(--brand-50))" stroke="rgb(var(--brand-600))" strokeWidth="2.2" />
        <rect x="44" y="63" width="8" height="18" rx="4" fill="rgb(var(--brand-100))" stroke="rgb(var(--brand-600))" strokeWidth="1.8" />
        <rect x="118" y="63" width="8" height="18" rx="4" fill="rgb(var(--brand-100))" stroke="rgb(var(--brand-600))" strokeWidth="1.8" />

        {/* eyes */}
        <rect x="67" y="65" width="9" height="11" rx="4.5" fill="rgb(var(--brand-600))" className="cqa-ld-blink" />
        <rect x="94" y="65" width="9" height="11" rx="4.5" fill="rgb(var(--brand-600))" className="cqa-ld-blink" />

        {/* mouth */}
        <rect x="73" y="84" width="24" height="4" rx="2" fill="rgb(var(--brand-300))" className="cqa-ld-talk" />
      </g>

      {/* the steps it is writing */}
      <rect x="35" y="114" width="70" height="6" rx="3" fill="rgb(var(--brand-200))" className="cqa-ld-write" />
      <rect x="35" y="126" width="100" height="6" rx="3" fill="rgb(var(--brand-200))" className="cqa-ld-write cqa-ld-d2" />
      <rect x="35" y="138" width="84" height="6" rx="3" fill="rgb(var(--brand-200))" className="cqa-ld-write cqa-ld-d4" />
    </svg>
  );
}

/* The same agent, finished.
 *
 * A success state must not reuse the working animation: an identical face
 * still blinking reads as "it is thinking" and leaves the user unsure whether
 * anything happened. What makes "done" unambiguous is a *change of state*
 * across three signals at once:
 *
 *   1. the eyes close into a contented arc — a face at rest, not scanning
 *   2. the thought bubbles are gone, because it has stopped deliberating
 *   3. the step lines it was writing are now complete, each with a tick, and a
 *      check badge sits on the head
 *
 * The ticks are what tie the face to the outcome. A smiling robot alone says
 * "I'm happy"; a smiling robot over three ticked-off lines says "I wrote your
 * tests". It is also deliberately still — motion signals work in progress, so
 * a finished state only animates once, on arrival.
 */
function BotDoneArt() {
  return (
    <svg
      className="cqa-ld"
      width="212"
      height="187"
      viewBox="0 0 170 150"
      role="presentation"
      aria-hidden="true"
    >
      {/* antenna, at rest */}
      <line x1="85" y1="48" x2="85" y2="37" stroke="rgb(var(--slate-400))" strokeWidth="2" strokeLinecap="round" />
      <circle cx="85" cy="33" r="3.4" fill="rgb(var(--brand-500))" />

      {/* head */}
      <rect x="54" y="48" width="62" height="50" rx="15" fill="rgb(var(--brand-50))" stroke="rgb(var(--brand-600))" strokeWidth="2.2" />
      <rect x="44" y="63" width="8" height="18" rx="4" fill="rgb(var(--brand-100))" stroke="rgb(var(--brand-600))" strokeWidth="1.8" />
      <rect x="118" y="63" width="8" height="18" rx="4" fill="rgb(var(--brand-100))" stroke="rgb(var(--brand-600))" strokeWidth="1.8" />

      {/* eyes closed into a smile — the face is at rest, not scanning */}
      <path d="M66 70 q5.5 -6 11 0" fill="none" stroke="rgb(var(--brand-600))" strokeWidth="2.6" strokeLinecap="round" />
      <path d="M93 70 q5.5 -6 11 0" fill="none" stroke="rgb(var(--brand-600))" strokeWidth="2.6" strokeLinecap="round" />
      <path d="M76 85 q9 7 18 0" fill="none" stroke="rgb(var(--brand-300))" strokeWidth="2.6" strokeLinecap="round" />

      {/* the badge that says the work landed */}
      <g className="cqa-ld-pop">
        <circle cx="117" cy="52" r="11" fill="rgb(var(--brand-600))" />
        <path d="M112 52.5 l3.4 3.4 l6.2 -6.8" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      </g>

      {/* the steps, written and ticked */}
      {[114, 126, 138].map((y, i) => (
        <g key={y} className={`cqa-ld-settle ${["", "cqa-ld-d1", "cqa-ld-d2"][i]}`}>
          <path
            d={`M31 ${y + 3} l3 3 l5.5 -6`}
            fill="none"
            stroke="rgb(var(--brand-600))"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <rect x="46" y={y} width={[70, 92, 78][i]} height="6" rx="3" fill="rgb(var(--brand-200))" />
        </g>
      ))}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Variants                                                           */
/* ------------------------------------------------------------------ */

const VARIANTS = {
  spark: {
    Art: () => <SparkArt />,
    title: "Loading…",
    lines: ["Please wait."],
    bar: false,
  },
  guide: {
    Art: GuideScanArt,
    title: "Reading your requirements guide…",
    lines: [
      "Drafting your Epics & Stories. This can take a moment —",
      "please wait, don't close this page.",
    ],
    bar: true,
  },
  xray: {
    Art: BotArt,
    title: "Writing your tests…",
    lines: ["The agent is authoring steps. Please wait."],
    bar: false,
  },
  "xray-done": {
    Art: BotDoneArt,
    title: "Your tests are written",
    lines: [],
    bar: false,
  },
};

/* Just the drawing, for callers that supply their own surrounding layout. */
function Art({ variant = "spark" }) {
  useLoaderStyles();
  const v = variantOf(variant);
  return <v.Art />;
}

function variantOf(name) {
  return VARIANTS[name] || VARIANTS.spark;
}

/* ------------------------------------------------------------------ */
/*  Inline — replaces a bare spinner in a button or a row              */
/* ------------------------------------------------------------------ */

function Inline({ size = 16, className = "" }) {
  useLoaderStyles();
  return (
    <svg
      className={["cqa-ld", className].filter(Boolean).join(" ")}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      role="status"
      aria-label="Loading"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3.5" opacity="0.2" />
      <path
        d="M 12 2 A 10 10 0 0 1 22 12"
        stroke="currentColor"
        strokeWidth="3.5"
        strokeLinecap="round"
        className="cqa-ld-spin"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Block — centred inside a panel or an empty state                   */
/* ------------------------------------------------------------------ */

function Block({ variant = "spark", label, sublabel, className = "" }) {
  useLoaderStyles();
  const v = variantOf(variant);
  const title = label !== undefined ? label : v.title;
  const lines = sublabel !== undefined ? [sublabel] : v.lines;

  return (
    <div
      className={[
        "flex flex-col items-center justify-center gap-3 px-6 py-10 text-center",
        className,
      ].filter(Boolean).join(" ")}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <v.Art />
      {title && <p className="text-sm font-semibold text-slate-900">{title}</p>}
      {lines.filter(Boolean).map((line, i) => (
        <p key={i} className="-mt-2 text-xs leading-relaxed text-slate-500">
          {line}
        </p>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Overlay — the blocking dialog                                      */
/* ------------------------------------------------------------------ */

/* Not dismissible, and that has to be enforced rather than merely implied:
 *  - no close button, and the backdrop ignores clicks
 *  - Escape is captured and stopped, so it cannot reach a parent dialog and
 *    close that instead (every modal in this app listens for Escape)
 *  - Tab is swallowed; there is nothing to focus in here and focus must not
 *    wander into the dimmed page behind
 *  - background scrolling is locked while it is up
 *
 * `delay` holds the overlay back briefly so an operation that turns out to be
 * fast doesn't produce a disorienting flash of dialog. Pass 0 to show at once.
 */
function Overlay({
  variant = "spark",
  label,
  sublabel,
  delay = 150,
  className = "",
  children,
}) {
  useLoaderStyles();
  const [visible, setVisible] = useState(delay === 0);
  const cardRef = useRef(null);

  useEffect(() => {
    if (delay === 0) return undefined;
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  /* Escape never gets through — it would close a dialog underneath instead of
     this one. Tab is kept inside: the overlay may carry a real action such as
     "Stop run", and swallowing Tab outright would make that button reachable
     only with a mouse. */
  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.key !== "Tab") return;

      // Nothing outside this dialog should receive focus while it is up.
      e.stopPropagation();
      const root = cardRef.current;
      const focusable = root
        ? root.querySelectorAll(
            'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          )
        : [];
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === root)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || active === root)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [visible]);

  // Lock background scroll, restoring whatever was there before.
  useEffect(() => {
    if (!visible) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [visible]);

  useEffect(() => {
    if (visible) cardRef.current?.focus();
  }, [visible]);

  if (!visible) return null;

  const v = variantOf(variant);
  const title = label !== undefined ? label : v.title;
  const lines = sublabel !== undefined ? [sublabel] : v.lines;

  /* Rendered on <body>, not where it is used: inside the page, an ancestor
     with its own stacking or containing block (a transform, a filter, a
     sticky header's layer) clips a `fixed` element, which left a strip of
     the header uncovered at the top. */
  return createPortal(
    <div
      className={[
        // Above the app's other dialogs (z-50) and the Inbox preview (z-60):
        // this one blocks whatever is already open.
        "fixed inset-0 z-[70] flex items-center justify-center p-4",
        className,
      ].filter(Boolean).join(" ")}
    >
      <div className="absolute inset-0 bg-overlay/50 backdrop-blur-sm" />
      <div
        ref={cardRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-busy="true"
        aria-live="polite"
        aria-label={title || "Loading"}
        className="relative z-10 w-full max-w-md rounded-2xl bg-card px-8 py-9 text-center shadow-xl outline-none ring-1 ring-slate-200"
      >
        <div className="flex justify-center">
          <v.Art />
        </div>
        {title && (
          <p className="mt-6 text-base font-semibold text-slate-900">{title}</p>
        )}
        {lines.filter(Boolean).map((line, i) => (
          <p key={i} className="mt-1.5 text-[0.8125rem] leading-relaxed text-slate-500">
            {line}
          </p>
        ))}
        {v.bar && (
          <div className="mx-auto mt-5 h-1.5 w-52 overflow-hidden rounded-full bg-slate-200">
            <div className="h-1.5 w-20 rounded-full bg-brand-600 cqa-ld-bar" />
          </div>
        )}
        {/* Room for whatever the caller needs to say or offer alongside the
            wait: the step it has reached, an error, a way to stop. A long wait
            with nothing but an animation is indistinguishable from a hang. */}
        {children}
      </div>
    </div>,
    document.body,
  );
}

/* ------------------------------------------------------------------ */

const Loader = Inline;
Loader.Inline = Inline;
Loader.Block = Block;
Loader.Overlay = Overlay;
Loader.Art = Art;

export { Inline, Block, Overlay, Art, VARIANTS };
export default Loader;
